import { useState, type FocusEvent, type KeyboardEvent, type PointerEvent } from 'react';

export type BucketPlacement = 'between' | 'centered';

const ratioAcross = (event: PointerEvent<HTMLElement>): number => {
  const box = event.currentTarget.getBoundingClientRect();
  return box.width > 0 ? (event.clientX - box.left) / box.width : 0;
};

const bucketAt = (ratio: number, count: number, placement: BucketPlacement): number => {
  const raw = placement === 'between' ? Math.round(ratio * (count - 1)) : Math.floor(ratio * count);
  return Math.min(Math.max(raw, 0), count - 1);
};

const KEY_STEPS: Readonly<Record<string, (current: number, count: number) => number>> = {
  ArrowRight: (current, count) => Math.min(current + 1, count - 1),
  ArrowLeft: (current) => Math.max(current - 1, 0),
  Home: () => 0,
  End: (_current, count) => count - 1,
};

export const useBucketFocus = (count: number, placement: BucketPlacement) => {
  const [active, setActive] = useState<number | null>(null);
  const clear = (): void => {
    setActive(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Escape') {
      clear();
      return;
    }
    const step = KEY_STEPS[event.key];
    if (step === undefined || count === 0) return;
    event.preventDefault();
    setActive(step(active ?? count - 1, count));
  };

  return {
    active: active !== null && active < count ? active : null,
    plotProps: {
      tabIndex: 0,
      onPointerMove: (event: PointerEvent<HTMLElement>) => {
        if (count > 0) setActive(bucketAt(ratioAcross(event), count, placement));
      },
      onPointerLeave: clear,
      onKeyDown,
      onFocus: (event: FocusEvent<HTMLElement>) => {
        if (event.currentTarget === event.target && count > 0) setActive(count - 1);
      },
      onBlur: clear,
    },
  };
};
